/**
 * ActivityCreateDialog 的业务状态与逻辑（从 ActivityCreateDialog.vue 抽离以控制单文件体积）
 */
import { createAct, updateAct } from '@/api/operation/activity'
import { useVipOptions } from '@/composables/useVipOptions'
import { centsToYuan, centsToYuanNumber, yuanToCents } from '@/utils/amount'
import { toUnixSeconds } from '@/utils/time'
import { Message } from 'element-ui'
import { computed, reactive, ref, watch } from 'vue'
import config from '@/config'
import { ACT_TYPE_OPTIONS } from '../const'
import { buildActivityCreateDialogFormRules } from './activityCreateDialogFormRules'
import { getActivityTypeModule, getAllActivityTypeModules } from './activity-types'

/** 与 ActivityCreateDialog 里 el-option :disabled 逻辑一致 */
function isActTypeOptionDisabled(value, p) {
  const v = Number(value)
  return (
    (v === 2 && p.hasRedEnvelopeRain) ||
    (v === 4 && p.hasSignIn) ||
    (v === 6 && p.hasAgentRewardBox) ||
    (v === 7 && p.hasLossCashback) ||
    (v === 8 && p.hasTotalDepositReward) ||
    (v === 9 && p.hasFirstDepositReward) ||
    (v === 1 && p.hasSpinLuck)
  )
}

/** 新建时默认选第一个未被禁用的活动类型 */
function getFirstSelectableActType(p) {
  for (let i = 0; i < ACT_TYPE_OPTIONS.length; i += 1) {
    const opt = ACT_TYPE_OPTIONS[i]
    if (!isActTypeOptionDisabled(opt.value, p)) return opt.value
  }
  return ACT_TYPE_OPTIONS[0]?.value ?? 1
}

const BONUS_GOLD = 1
const BONUS_NONE = -1

const SIGN_IN_METHOD_FIXED_DAYS = 1
const SIGN_IN_REWARD_TYPE_COIN = 1
const SIGN_IN_REWARD_TYPE_NONE = -1
const SIGN_IN_REWARD_MODE_FIXED = 1
const SIGN_IN_REWARD_MODE_RANDOM = 2

const TIER_COUNT = 8

const RED_REWARD_TYPE_RANDOM = 1
const RED_REWARD_TYPE_FIXED = 2
const RED_REWARD_TYPE_GUARANTEE = 3

const RED_NUM_INTERVAL_GTE = 1
const RED_NUM_INTERVAL_GT = 2

function pad2(n) {
  return n < 10 ? `0${n}` : String(n)
}

function toPickerDateTime(ts) {
  if (!ts) return ''
  const n = Number(ts)
  if (!Number.isFinite(n) || n <= 0) return ''
  const ms = String(Math.trunc(n)).length === 10 ? n * 1000 : n
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(
    d.getMinutes()
  )}:${pad2(d.getSeconds())}`
}

export function useActivityCreateDialog(props, emit) {
  const descLangOptions = Array.isArray(config.languages) ? config.languages : []
  const defaultDescLang = descLangOptions[0]?.value || 'en'

  function buildDefaultDescValue() {
    const opts = descLangOptions || []
    const obj = {}
    for (let i = 0; i < opts.length; i += 1) {
      const k = opts[i]?.value
      if (k != null) obj[k] = ''
    }
    return obj
  }

  const { vipOptions, refresh: refreshVipOptions } = useVipOptions()

  const formRef = ref(null)
  const submitting = ref(false)
  const dialogTitle = computed(() => (props.mode === 'edit' ? '编辑' : '新增'))

  const ruleFlags = reactive({
    login: false,
    bet: false,
    payin: false,
    level: false,
  })

  const ruleValues = reactive({
    daily_login_bonus_count: undefined,
    daily_bet_amount: undefined,
    daily_bet_amount_ticket_reward: undefined,
    daily_total_payin_amount: undefined,
    daily_total_payin_amount_ticket_reward: undefined,
    level_up_bonus_count: undefined,
  })

  const redRuleFlags = reactive({
    bet: false,
    payin: false,
  })

  const redRuleValues = reactive({
    today_bet_amount: undefined,
    today_payin_amount: undefined,
    count_limit: 0,
    countdown: 0,
    duration: 0,
    multiple: 0,
  })

  const agentBoxRuleFlags = reactive({
    bet: false,
    payin: false,
  })

  const agentBoxRuleValues = reactive({
    bet_amount: undefined,
    payin_amount: undefined,
    multiple: 1,
  })

  const betRebateRule = reactive({
    multiple: 1,
  })

  const payinRebateRule = reactive({
    calculate_type: 1,
    cycle_days: 1,
    multiple: 1,
  })

  const firstDepositRule = reactive({
    multiple: 1,
    display_time: 7,
  })

  function createEmptyTier() {
    return {
      bonus_type: BONUS_NONE,
      reward_count_yuan: undefined,
      multiple: 1,
      available_num: -1,
      total_unlimited: true,
      weight: undefined,
    }
  }

  function buildDefaultTiers() {
    return Array.from({ length: TIER_COUNT }, () => createEmptyTier())
  }

  function createDefaultRedGuaranteeTier() {
    return {
      isGuarantee: true,
      num_interval_type: RED_NUM_INTERVAL_GTE,
      num: 0,
      reward_type: RED_REWARD_TYPE_GUARANTEE,
      reward_count_min: 0,
      reward_count_max: 0,
      reward_count: 20,
      total: 0,
      total_unlimited: true,
      available_num: 0,
      weight: 0,
      id: '',
    }
  }

  function buildDefaultRedTiers() {
    return [createDefaultRedGuaranteeTier()]
  }

  function createEmptySignInTier(dayIndex) {
    return {
      day_index: dayIndex,
      reward_type: SIGN_IN_REWARD_TYPE_COIN,
      reward_mode: SIGN_IN_REWARD_MODE_FIXED,
      gold: undefined,
      gold_min: undefined,
      gold_max: undefined,
      multiple: 1,
      settled_count: 0,
    }
  }

  function buildDefaultSignInTiers(cycleDays) {
    const n = Number(cycleDays)
    const dayCount = Number.isFinite(n) && n > 0 ? n : 1
    return Array.from({ length: dayCount }, (_, i) => {
      const dayIndex = i + 1
      return createEmptySignInTier(dayIndex)
    })
  }

  const localForm = reactive({
    id: null,
    act_id: null,
    title: buildDefaultDescValue(),
    icon: '',
    desc: buildDefaultDescValue(),
    desc_lang: defaultDescLang,
    actType: getFirstSelectableActType(props),
    periodType: 'range',
    activityTimeRange: [],
    displayTimeRange: [],
    displayTimeRangeText: '',
    status: 2,
    sort: 0,
    reward_expire_days: 0,
    vips: [],
    betRebateRule,
    payinRebateRule,
    firstDepositRule,
    firstDepositTiers: [
      {
        id: '',
        amount_yuan: 100,
        reward_type: 2,
        reward_count_yuan: undefined,
        reward_count_min: undefined,
        reward_count_max: undefined,
      },
    ],
    tiers: buildDefaultTiers(),
    redTiers: buildDefaultRedTiers(),
    signInRule: {
      method: SIGN_IN_METHOD_FIXED_DAYS,
      cycle_days: 1,
    },
    signInTiers: buildDefaultSignInTiers(1),
    spinRuleAtLeastOne: 0,
    ruleFlags,
    ruleValues,
    redRuleFlags,
    redRuleValues,
    url_type: 'inner',
    jump_url: '',
    agentBoxRuleFlags,
    agentBoxRuleValues,
    betRebateTiers: [{ id: '', threshold_yuan: 100, gold_yuan: undefined, settled_count: 0 }],
    payinRebateTiers: [{ id: '', threshold_yuan: 100, gold_yuan: undefined, settled_count: 0 }],
    agentBoxTiers: [
      {
        id: '',
        need_share_one_num: 1,
        num_interval_type: 1,
        reward_type: 1,
        reward_count_min: 0,
        reward_count_max: 0,
        reward_count: 0,
        available_num: 0,
      },
    ],
  })

  const actType = computed(() => {
    if (props.mode === 'edit') {
      return props.activityData?.type
    }
    return localForm.actType
  })

  const currentTypeModule = computed(() => getActivityTypeModule(actType.value))

  function syncVipCheckAllState() {
    formRef.value && formRef.value.validateField('vips')
  }

  function onVipCheckAllChange(val) {
    const opts = vipOptions.value || []
    localForm.vips = val ? opts.map(o => o.value) : []
    syncVipCheckAllState()
  }

  const formRules = buildActivityCreateDialogFormRules({
    localForm,
    ruleFlags,
    ruleValues,
    redRuleFlags,
    redRuleValues,
  })

  function parseTierWeight(w) {
    const n = Number(w)
    return Number.isFinite(n) && n > 0 ? n : 0
  }

  function normalizeTierForForm(tier) {
    const bonusTypeRaw = tier?.bonus_type
    const bonusType = bonusTypeRaw === BONUS_GOLD ? BONUS_GOLD : BONUS_NONE
    const available_num = Number(tier?.available_num)
    return {
      bonus_type: bonusType,
      reward_count_yuan: bonusType === BONUS_GOLD ? centsToYuanNumber(tier?.reward_count) : undefined,
      multiple: bonusType === BONUS_GOLD ? Number(tier?.multiple) || 1 : 0,
      available_num,
      total_unlimited: available_num === -1,
      settled_count: Number(tier?.settled_count) || 0,
      weight: Number.isFinite(Number(tier?.weight)) ? Number(tier?.weight) : undefined,
    }
  }

  function normalizeRedTierDistributedNum(tier) {
    const raw = tier?.available_num
    if (raw !== undefined && raw !== null && raw !== '') {
      const n = Number(raw)
      return Number.isFinite(n) ? n : 0
    }
    return 0
  }

  function normalizeRedTierForForm(tier) {
    const rewardType = Number(tier?.reward_type) || RED_REWARD_TYPE_RANDOM
    const totalRaw = Number(tier?.total)
    const total_unlimited = !Number.isFinite(totalRaw) || totalRaw < 0

    const id = tier?.id || ''
    const rawWeight = Number.isFinite(Number(tier?.weight)) ? Number(tier?.weight) : undefined
    const num_interval_type = Number.isFinite(Number(tier?.num_interval_type))
      ? Number(tier?.num_interval_type)
      : RED_NUM_INTERVAL_GTE
    const num = Number.isFinite(Number(tier?.num)) ? Number(tier?.num) : 0
    const distributedNum = normalizeRedTierDistributedNum(tier)

    if (rewardType === RED_REWARD_TYPE_RANDOM) {
      let min = 0
      let max = 0
      if (Array.isArray(tier?.reward_count_arr) && tier.reward_count_arr.length === 2) {
        min = centsToYuanNumber(Number(tier.reward_count_arr[0]) || 0)
        max = centsToYuanNumber(Number(tier.reward_count_arr[1]) || 0)
      } else {
        const rc = centsToYuanNumber(Number(tier?.reward_count)) || 0
        min = rc
        max = rc
      }
      return {
        isGuarantee: false,
        id,
        num_interval_type,
        num,
        reward_type: RED_REWARD_TYPE_RANDOM,
        reward_count_min: min,
        reward_count_max: max,
        reward_count: min,
        total: total_unlimited ? 0 : totalRaw,
        total_unlimited,
        available_num: distributedNum,
        weight: rawWeight != null ? rawWeight : 1,
      }
    }

    const rc = centsToYuanNumber(Number(tier?.reward_count)) || 0
    const isGuarantee = rewardType === RED_REWARD_TYPE_GUARANTEE
    return {
      isGuarantee,
      id,
      num_interval_type,
      num,
      reward_type: isGuarantee ? RED_REWARD_TYPE_GUARANTEE : RED_REWARD_TYPE_FIXED,
      reward_count_min: rc,
      reward_count_max: rc,
      reward_count: rc,
      total: total_unlimited ? 0 : totalRaw,
      total_unlimited,
      available_num: distributedNum,
      weight: isGuarantee ? 0 : rawWeight != null ? rawWeight : 1,
    }
  }

  function buildRulePayload() {
    const rule = {}
    if (ruleFlags.login && ruleValues.daily_login_bonus_count != null) {
      rule.daily_login_bonus_count = Number(ruleValues.daily_login_bonus_count) || 0
    }
    if (ruleFlags.bet && ruleValues.daily_bet_amount != null) {
      rule.daily_bet_amount = yuanToCents(ruleValues.daily_bet_amount)
      rule.daily_bet_amount_ticket_reward = Number(ruleValues.daily_bet_amount_ticket_reward) || 0
    }
    if (ruleFlags.payin && ruleValues.daily_total_payin_amount != null) {
      rule.daily_total_payin_amount = yuanToCents(ruleValues.daily_total_payin_amount)
      rule.daily_total_payin_amount_ticket_reward = Number(ruleValues.daily_total_payin_amount_ticket_reward) || 0
    }
    if (ruleFlags.level && ruleValues.level_up_bonus_count != null) {
      rule.level_up_bonus_count = Number(ruleValues.level_up_bonus_count) || 0
    }
    return rule
  }

  function buildRedRulePayload() {
    const rule = {}
    if (redRuleFlags.bet && redRuleValues.today_bet_amount != null) {
      rule.today_bet_amount = yuanToCents(redRuleValues.today_bet_amount)
    }
    if (redRuleFlags.payin && redRuleValues.today_payin_amount != null) {
      rule.today_payin_amount = yuanToCents(redRuleValues.today_payin_amount)
    }

    rule.count_limit = Number(redRuleValues.count_limit) || 0
    rule.countdown = Number(redRuleValues.countdown) || 0
    rule.duration = Number(redRuleValues.duration) || 0
    rule.multiple = Number(redRuleValues.multiple) || 0
    return rule
  }

  function buildRedTiersPayload() {
    return localForm.redTiers.map(t => {
      const isGuarantee = Boolean(t.isGuarantee) || Number(t.reward_type) === RED_REWARD_TYPE_GUARANTEE
      const base = {
        id: t.id || '',
        num_interval_type: Number(t.num_interval_type) || RED_NUM_INTERVAL_GTE,
        num: Number(t.num) || 0,
        reward_type: isGuarantee ? RED_REWARD_TYPE_GUARANTEE : Number(t.reward_type) || RED_REWARD_TYPE_RANDOM,
        total: t.total_unlimited || isGuarantee ? -1 : Number(t.total) || 0,
        weight: isGuarantee ? 0 : Number(t.weight) || 0,
      }

      if (base.reward_type === RED_REWARD_TYPE_RANDOM) {
        base.reward_count_arr = [
          yuanToCents(Number(t.reward_count_min)) || 0,
          yuanToCents(Number(t.reward_count_max)) || 0,
        ]
      } else {
        base.reward_count = yuanToCents(Number(t.reward_count)) || 0
      }

      return base
    })
  }

  function buildTypeModuleCtx() {
    return {
      Message,
      centsToYuan,
      centsToYuanNumber,
      yuanToCents,
      toPickerDateTime,
      toUnixSeconds,
      BONUS_GOLD,
      BONUS_NONE,
      TIER_COUNT,
      RED_REWARD_TYPE_RANDOM,
      RED_REWARD_TYPE_FIXED,
      RED_REWARD_TYPE_GUARANTEE,
      RED_NUM_INTERVAL_GTE,
      RED_NUM_INTERVAL_GT,
      SIGN_IN_METHOD_FIXED_DAYS,
      SIGN_IN_REWARD_TYPE_COIN,
      SIGN_IN_REWARD_TYPE_NONE,
      SIGN_IN_REWARD_MODE_FIXED,
      SIGN_IN_REWARD_MODE_RANDOM,
      ruleFlags,
      ruleValues,
      redRuleFlags,
      redRuleValues,
      parseTierWeight,
      firstDepositRule,
      normalizeTierForForm,
      normalizeRedTierForForm,
      createEmptyTier,
      createDefaultRedGuaranteeTier,
      buildDefaultTiers,
      buildDefaultRedTiers,
      buildDefaultSignInTiers,
      buildRulePayload,
      buildRedRulePayload,
      buildRedTiersPayload,
    }
  }

  function resetForm() {
    localForm.id = null
    localForm.act_id = null
    localForm.title = buildDefaultDescValue()
    localForm.icon = ''
    localForm.desc = buildDefaultDescValue()
    localForm.desc_lang = defaultDescLang
    localForm.actType = getFirstSelectableActType(props)
    localForm.periodType = 'range'
    localForm.activityTimeRange = []
    localForm.status = 2
    localForm.sort = 0
    localForm.reward_expire_days = 0
    localForm.displayTimeRange = []
    localForm.displayTimeRangeText = ''
    localForm.vips = []
    localForm.tiers = buildDefaultTiers()
    localForm.redTiers = buildDefaultRedTiers()
    localForm.signInRule.method = SIGN_IN_METHOD_FIXED_DAYS
    localForm.signInRule.cycle_days = 1
    localForm.signInTiers = buildDefaultSignInTiers(1)
    ruleFlags.login = false
    ruleFlags.bet = false
    ruleFlags.payin = false
    ruleFlags.level = false
    localForm.spinRuleAtLeastOne = 0
    redRuleFlags.bet = false
    redRuleFlags.payin = false
    ruleValues.daily_login_bonus_count = undefined
    ruleValues.daily_bet_amount = undefined
    ruleValues.daily_bet_amount_ticket_reward = undefined
    ruleValues.daily_total_payin_amount = undefined
    ruleValues.daily_total_payin_amount_ticket_reward = undefined
    ruleValues.level_up_bonus_count = undefined
    redRuleValues.today_bet_amount = undefined
    redRuleValues.today_payin_amount = undefined
    redRuleValues.count_limit = 0
    redRuleValues.countdown = 0
    redRuleValues.duration = 0
    redRuleValues.multiple = 0
    localForm.url_type = 'inner'
    localForm.jump_url = ''
    const ctx = buildTypeModuleCtx()
    getAllActivityTypeModules().forEach(m => {
      if (typeof m.reset === 'function') m.reset(localForm, ctx)
    })
    formRef.value && formRef.value.clearValidate()
  }

  function fillFormByActivity(raw) {
    if (!raw || typeof raw !== 'object') return
    localForm.id = raw.id != null ? raw.id : null
    localForm.act_id = raw.act_id != null ? raw.act_id : null
    localForm.desc_lang = raw.desc_lang || defaultDescLang

    if (raw.title && typeof raw.title === 'object') {
      localForm.title = { ...(localForm.title || {}), ...raw.title }
    } else {
      localForm.title = buildDefaultDescValue()
      localForm.title[localForm.desc_lang] = raw.title || ''
    }
    localForm.icon = raw.icon || raw.act_icon || ''
    localForm.desc = buildDefaultDescValue()
    if (raw.desc && typeof raw.desc === 'object') {
      localForm.desc = { ...localForm.desc, ...raw.desc }
    } else if (typeof raw.desc === 'string') {
      localForm.desc[localForm.desc_lang] = raw.desc
    }
    const type = Number(raw.type)
    localForm.actType = Number.isFinite(type) ? type : 1
    localForm.status = raw.status != null ? raw.status : 2
    localForm.sort = Number(raw.sort) >= 0 ? Number(raw.sort) : 0

    localForm.periodType = 'range'
    //暂时去掉永久日期0423
    const startPicker = toPickerDateTime(raw.start_at)
    const endPicker = toPickerDateTime(raw.end_at)
    localForm.activityTimeRange = [startPicker, endPicker]
    // if (startPicker && endPicker) {
    //   localForm.periodType = 'range'
    //   localForm.activityTimeRange = [startPicker, endPicker]
    // } else {
    //   localForm.periodType = 'permanent'
    //   localForm.activityTimeRange = []
    // }

    // 回填展示时间（时间范围，单位：秒）
    const displayStart = raw.show_start_at
    const displayEnd = raw.show_end_at
    if (displayStart && displayEnd) {
      const startStr = toPickerDateTime(displayStart)
      const endStr = toPickerDateTime(displayEnd)
      if (startStr && endStr) {
        localForm.displayTimeRange = [startStr, endStr]
        localForm.displayTimeRangeText = `${startStr} 至 ${endStr}`
      }
    }

    const hours = Number(raw.reward_expire_at_hours)
    localForm.reward_expire_days = Number.isFinite(hours) && hours > 0 ? hours / 24 : 0

    const ctx = buildTypeModuleCtx()
    const module = getActivityTypeModule(localForm.actType)
    if (module && typeof module.fillFromActivity === 'function') {
      module.fillFromActivity(localForm, raw, ctx)
    }
  }

  function handleClose() {
    emit('update:visible', false)
  }

  function buildPayload() {
    const payload = {
      title: localForm.title,
      icon: localForm.icon || '',
      desc: localForm.desc,
      type: localForm.actType,
      status: localForm.status,
      sort: Number(localForm.sort) || 0,
      reward_expire_at_hours: Math.round(Number(localForm.reward_expire_days) * 24) || 0,
    }

    const ctx = buildTypeModuleCtx()
    const module = getActivityTypeModule(localForm.actType)
    if (module && typeof module.buildPayload === 'function') {
      Object.assign(payload, module.buildPayload(localForm, ctx))
    }

    if (
      localForm.periodType === 'range' &&
      Array.isArray(localForm.activityTimeRange) &&
      localForm.activityTimeRange.length === 2
    ) {
      const start = toUnixSeconds(localForm.activityTimeRange[0])
      const end = toUnixSeconds(localForm.activityTimeRange[1])
      if (start) payload.start_at = start
      if (end) payload.end_at = end
    } else {
      payload.start_at = -1
      payload.end_at = -1
    }
    if (props.mode === 'edit') {
      if (localForm.id != null) payload.id = localForm.id
      if (localForm.act_id != null) payload.act_id = localForm.act_id
    }
    return payload
  }

  function handleSubmit() {
    if (!formRef.value) return
    formRef.value.validate((valid, errors) => {
      if (!valid) {
        if (errors.spinRuleAtLeastOne && localForm.actType === 1) {
          Message.error(errors.spinRuleAtLeastOne[0].message)
          return
        }
        if (errors.redRuleAtLeastOne && localForm.actType === 2) {
          Message.error(errors.redRuleAtLeastOne[0].message)
          return
        }
        return
      }
      const ctx = buildTypeModuleCtx()
      const module = getActivityTypeModule(localForm.actType)
      if (module && typeof module.validate === 'function') {
        if (!module.validate(localForm, ctx)) return
      }
      submitting.value = true
      const api = props.mode === 'create' ? createAct : updateAct
      api(buildPayload())
        .then(() => {
          Message.success(props.mode === 'create' ? '新增成功' : '保存成功')
          emit('success')
          handleClose()
        })
        .catch(() => {})
        .finally(() => {
          submitting.value = false
        })
    })
  }

  watch(
    () => props.visible,
    async v => {
      if (v) {
        resetForm()
        await refreshVipOptions()
        if (props.mode === 'edit') {
          fillFormByActivity(props.activityData)
        }
      }
    }
  )

  watch(
    () => localForm.actType,
    v => {
      if (props.mode === 'edit') return
      const ctx = buildTypeModuleCtx()
      const module = getActivityTypeModule(v)
      if (module && typeof module.reset === 'function') module.reset(localForm, ctx)
      formRef.value && formRef.value.clearValidate()
    }
  )

  watch(
    () => localForm.periodType,
    val => {
      if (val === 'permanent') {
        localForm.activityTimeRange = []
      }
      formRef.value && formRef.value.clearValidate(['activityTimeRange'])
    }
  )

  watch(
    () => localForm.signInRule.cycle_days,
    val => {
      const n = Number(val)
      if (!Number.isFinite(n) || n < 1) return

      const oldMap = new Map((localForm.signInTiers || []).map(t => [Number(t.day_index), t]))
      localForm.signInTiers = Array.from({ length: n }, (_, i) => {
        const dayIndex = i + 1
        const old = oldMap.get(dayIndex)
        if (!old) return createEmptySignInTier(dayIndex)
        const { isFullSign, ...rest } = old
        void isFullSign
        return { ...rest, day_index: dayIndex }
      })

      formRef.value && formRef.value.clearValidate(['signInRule.cycle_days', 'signInRule.method'])
    }
  )

  return {
    formRef,
    submitting,
    dialogTitle,
    vipOptions,
    localForm,
    actType,
    currentTypeModule,
    ruleFlags,
    ruleValues,
    redRuleFlags,
    redRuleValues,
    formRules,
    syncVipCheckAllState,
    onVipCheckAllChange,
    handleClose,
    handleSubmit,
  }
}
